import React, { useEffect, useMemo, useRef, useState } from 'react'
import { InfoIcon } from '../../../lib/components/Icon/icon-component'
import { ToolTip } from '../../../lib/components/ToolTip'
import { OpportunityEstimatedFinancialsProps } from '../../../lib/types'
import OpportunityEstimatedFinancialsChart from './opportunity-detail-estimated-financials-chart'
import {
  ESTIMATED_FINANCIALS_TOOLTIP,
  buildMetricComparisonChartData,
  formatFinancialMetricLabel,
  getEstimatedFinancialDisclaimer,
  getEstimatedFinancialMetricColor,
  getEstimatedFinancialsCurrencyCode,
  getEstimatedFinancialsSection,
  getMetricChartPoints,
  getRenderableFinancialMetrics,
  getEffectiveComparisonGraphType,
  parseMetricGraphType,
} from './utils'
import { OPPORTUNITY_SECTION_IDS } from '../opportunity-detail.utils'

const NO_COMPARISON_METRIC = ''

type MetricFilterDropdownProps = {
  label: string
  selectedLabel: string
  options: { metricName: string; label: string }[]
  selectedMetricName: string
  isOpen: boolean
  onToggle: () => void
  onSelect: (metricName: string) => void
  testIdPrefix: string
}

const MetricFilterDropdown = ({
  label,
  selectedLabel,
  options,
  selectedMetricName,
  isOpen,
  onToggle,
  onSelect,
  testIdPrefix,
}: MetricFilterDropdownProps) => (
  <div className='opportunity-estimated-financials-filter'>
    <span className='opportunity-estimated-financials-filter-label'>{label}</span>
    <button
      type='button'
      className='opportunity-estimated-financials-filter-trigger'
      aria-expanded={isOpen}
      aria-haspopup='listbox'
      data-testid={`${testIdPrefix}-trigger`}
      onClick={onToggle}
    >
      <span>{selectedLabel}</span>
      <span
        className={`opportunity-estimated-financials-filter-chevron${isOpen ? ' is-open' : ''}`}
        aria-hidden='true'
      />
    </button>
    {isOpen ? (
      <ul
        className='opportunity-estimated-financials-filter-menu'
        role='listbox'
        data-testid={`${testIdPrefix}-menu`}
      >
        {options.map((option) => {
          const isActive = option.metricName === selectedMetricName

          return (
            <li key={option.metricName || 'none'} role='presentation'>
              <button
                type='button'
                role='option'
                aria-selected={isActive}
                className={isActive ? 'is-active' : ''}
                data-testid={`${testIdPrefix}-option-${option.metricName}`}
                onClick={() => onSelect(option.metricName)}
              >
                {option.label}
              </button>
            </li>
          )
        })}
      </ul>
    ) : null}
  </div>
)

const OpportunityEstimatedFinancials = ({
  opportunityDetails,
  onMissingDataClick,
}: OpportunityEstimatedFinancialsProps) => {
  const section = getEstimatedFinancialsSection(opportunityDetails)
  const metrics = useMemo(() => getRenderableFinancialMetrics(section), [section])
  const [selectedMetricName, setSelectedMetricName] = useState('')
  const [compareMetricName, setCompareMetricName] = useState(NO_COMPARISON_METRIC)
  const [isMetricFilterOpen, setIsMetricFilterOpen] = useState(false)
  const [isCompareFilterOpen, setIsCompareFilterOpen] = useState(false)
  const filtersRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!metrics.length) {
      return
    }

    if (!metrics.some((metric) => metric.metricName === selectedMetricName)) {
      setSelectedMetricName(metrics[0].metricName)
    }
  }, [metrics, selectedMetricName])

  const selectedMetric = useMemo(
    () => metrics.find((metric) => metric.metricName === selectedMetricName) ?? metrics[0],
    [metrics, selectedMetricName],
  )
  const graphType = parseMetricGraphType(selectedMetric?.graphType)
  const showComparisonFilter = metrics.length > 1
  const compareMetricOptions = useMemo(
    () => metrics.filter((metric) => metric.metricName !== selectedMetric?.metricName),
    [metrics, selectedMetric],
  )
  const isComparing =
    showComparisonFilter &&
    compareMetricName !== NO_COMPARISON_METRIC &&
    compareMetricOptions.some((metric) => metric.metricName === compareMetricName)

  useEffect(() => {
    if (
      compareMetricName !== NO_COMPARISON_METRIC &&
      !compareMetricOptions.some((metric) => metric.metricName === compareMetricName)
    ) {
      setCompareMetricName(NO_COMPARISON_METRIC)
    }
  }, [compareMetricName, compareMetricOptions])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target as Node)) {
        setIsMetricFilterOpen(false)
        setIsCompareFilterOpen(false)
      }
    }

    if (isMetricFilterOpen || isCompareFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isCompareFilterOpen, isMetricFilterOpen])

  const selectedMetricIndex = useMemo(
    () => metrics.findIndex((metric) => metric.metricName === selectedMetric?.metricName),
    [metrics, selectedMetric],
  )
  const compareMetric = useMemo(
    () =>
      isComparing ? metrics.find((metric) => metric.metricName === compareMetricName) : undefined,
    [compareMetricName, isComparing, metrics],
  )
  const compareMetricIndex = useMemo(
    () => metrics.findIndex((metric) => metric.metricName === compareMetric?.metricName),
    [compareMetric, metrics],
  )

  const chartData = useMemo(() => {
    if (!selectedMetric) {
      return []
    }

    if (isComparing && compareMetric) {
      return buildMetricComparisonChartData(selectedMetric, compareMetric)
    }

    return getMetricChartPoints(selectedMetric)
  }, [compareMetric, isComparing, selectedMetric])

  const currencyCode = getEstimatedFinancialsCurrencyCode(selectedMetric, section)
  const barColor = getEstimatedFinancialMetricColor(
    selectedMetric ?? metrics[0],
    Math.max(selectedMetricIndex, 0),
  )
  const metricLabel = selectedMetric
    ? formatFinancialMetricLabel(selectedMetric.metricName)
    : 'Metric'
  const compareMetricLabel = isComparing
    ? compareMetric
      ? formatFinancialMetricLabel(compareMetric.metricName)
      : 'Compare with'
    : 'None'
  const comparisonSeries =
    isComparing && compareMetric
      ? {
          metricName: compareMetric.metricName,
          label: compareMetricLabel,
          currencyCode: getEstimatedFinancialsCurrencyCode(compareMetric, section),
          barColor: getEstimatedFinancialMetricColor(
            compareMetric,
            Math.max(compareMetricIndex, 0),
          ),
        }
      : undefined
  const chartGraphType = comparisonSeries ? getEffectiveComparisonGraphType(graphType) : graphType

  const heading = section?.heading?.trim() || 'Estimated Financials'
  const disclaimer = getEstimatedFinancialDisclaimer(section)
  const showMetricFilter = metrics.length > 1
  const metricFilterOptions = metrics.map((metric) => ({
    metricName: metric.metricName,
    label: formatFinancialMetricLabel(metric.metricName),
  }))
  const compareFilterOptions = [
    { metricName: NO_COMPARISON_METRIC, label: 'None' },
    ...compareMetricOptions.map((metric) => ({
      metricName: metric.metricName,
      label: formatFinancialMetricLabel(metric.metricName),
    })),
  ]

  if (!section?.showSection || !metrics.length || !selectedMetric) {
    return null
  }

  return (
    <section
      id={OPPORTUNITY_SECTION_IDS.estimatedFinancials}
      className='opportunity-overview-panel opportunity-cms-section-card opportunity-estimated-financials-card'
      data-testid='estimated-financials-section'
    >
      <header className='opportunity-estimated-financials-header'>
        <section className='opportunity-estimated-financials-title-row'>
          <h2 className='opportunity-cms-section-title h5-heading'>{heading}</h2>
          <ToolTip content={ESTIMATED_FINANCIALS_TOOLTIP} clickable={false}>
            <button
              type='button'
              className='opportunity-estimated-financials-info-btn'
              aria-label='Estimated financials information'
              data-testid='estimated-financials-info'
            >
              <InfoIcon
                width='18'
                height='18'
                classes={['opportunity-estimated-financials-info-icon']}
              />
            </button>
          </ToolTip>
        </section>

        {showMetricFilter || showComparisonFilter ? (
          <div
            className='opportunity-estimated-financials-filters'
            ref={filtersRef}
            data-testid='estimated-financials-filters'
          >
            {showMetricFilter ? (
              <MetricFilterDropdown
                label='Metric'
                selectedLabel={metricLabel}
                options={metricFilterOptions}
                selectedMetricName={selectedMetric.metricName}
                isOpen={isMetricFilterOpen}
                onToggle={() => {
                  setIsMetricFilterOpen((open) => !open)
                  setIsCompareFilterOpen(false)
                }}
                onSelect={(metricName) => {
                  setSelectedMetricName(metricName)
                  setIsMetricFilterOpen(false)
                }}
                testIdPrefix='estimated-financials-filter'
              />
            ) : null}
            {showComparisonFilter ? (
              <MetricFilterDropdown
                label='Compare with'
                selectedLabel={compareMetricLabel}
                options={compareFilterOptions}
                selectedMetricName={
                  isComparing ? compareMetric?.metricName ?? '' : NO_COMPARISON_METRIC
                }
                isOpen={isCompareFilterOpen}
                onToggle={() => {
                  setIsCompareFilterOpen((open) => !open)
                  setIsMetricFilterOpen(false)
                }}
                onSelect={(metricName) => {
                  setCompareMetricName(metricName)
                  setIsCompareFilterOpen(false)
                }}
                testIdPrefix='estimated-financials-compare-filter'
              />
            ) : null}
          </div>
        ) : null}
      </header>

      <section className='opportunity-estimated-financials-chart'>
        <OpportunityEstimatedFinancialsChart
          graphType={chartGraphType}
          data={chartData}
          metricName={selectedMetric.metricName}
          currencyCode={currencyCode}
          barColor={barColor}
          label={metricLabel}
          comparisonSeries={comparisonSeries}
        />
      </section>

      <footer className='opportunity-estimated-financials-footer'>
        <p
          className='opportunity-estimated-financials-disclaimer'
          data-testid='estimated-financials-disclaimer'
        >
          {disclaimer}
        </p>
        {onMissingDataClick ? (
          <button
            type='button'
            className='opportunity-estimated-financials-feedback-link'
            data-testid='estimated-financials-feedback'
            onClick={onMissingDataClick}
          >
            <span className='opportunity-estimated-financials-feedback-emphasis'>Let us know</span>
            <span> if we are missing anything</span>
          </button>
        ) : null}
      </footer>
    </section>
  )
}

export default OpportunityEstimatedFinancials
